import { safeStorage } from './safariStorage';

export interface RateLimitInfo {
  isRateLimited: boolean;
  type: 'ip' | 'email' | 'unknown';
  retryAfterSeconds: number | null;
  message: string;
}

export const parseRateLimitError = (error: any): RateLimitInfo => {
  // Extract error details for precise detection
  const errorMessage = (error?.message || error?.error_description || '').toLowerCase();
  const errorCode = error?.code || '';
  
  // Only detect ACTUAL rate limit errors - NOT all 422s (which can be validation errors)
  const isRateLimit = 
    // HTTP 429 is always a rate limit
    error?.status === 429 ||
    // Specific Supabase rate limit error codes
    errorCode === 'over_request_limit' ||
    errorCode === 'over_email_send_rate_limit' ||
    // Explicit rate limit messages
    errorMessage.includes('rate limit') ||
    errorMessage.includes('too many requests') ||
    // Only treat 422 as rate limit if message explicitly mentions email rate limiting
    (error?.status === 422 && (
      errorMessage.includes('email rate limit') ||
      errorMessage.includes('over_email_send_rate_limit') ||
      errorMessage.includes('email sending rate')
    ));

  if (!isRateLimit) {
    return { isRateLimited: false, type: 'unknown', retryAfterSeconds: null, message: '' };
  }

  // Try to extract retry-after from various sources
  let retryAfterSeconds: number | null = null;
  
  // Check response headers (if available)
  if (error?.headers?.get) {
    const retryAfter = error.headers.get('retry-after');
    if (retryAfter) {
      retryAfterSeconds = parseInt(retryAfter, 10);
    }
  }
  
  // Check error body for retry info
  if (error?.retryAfter) {
    retryAfterSeconds = error.retryAfter;
  }

  // Determine if it's IP-based (Supabase server) or email-based (our client)
  // Supabase returns 429 for IP-based limits
  const isIpBased = error?.status === 429 || 
                    error?.message?.includes('request limit') ||
                    !error?.message?.includes('email');

  const type = isIpBased ? 'ip' : 'email';
  
  // Generate appropriate message based on rate limit type
  const message = type === 'ip'
    ? `You've reached the server's rate limit. This is based on your IP address, so using a different email won't help. Please wait ${retryAfterSeconds ? formatWaitTime(retryAfterSeconds) : '15-30 minutes'} before trying again.`
    : `You've made too many attempts with this email. Please wait ${retryAfterSeconds ? formatWaitTime(retryAfterSeconds) : 'a few minutes'} or try a different email address.`;

  return {
    isRateLimited: true,
    type,
    retryAfterSeconds: retryAfterSeconds || (type === 'ip' ? 900 : 300), // Default: 15min for IP, 5min for email
    message
  };
};

export const formatWaitTime = (seconds: number): string => {
  if (seconds < 60) return `${seconds} seconds`;
  const minutes = Math.ceil(seconds / 60);
  return minutes === 1 ? '1 minute' : `${minutes} minutes`;
};

// Storage for rate limit state to persist across page refreshes
export const rateLimitStorage = {
  setRateLimited: (type: 'signup' | 'login', expiresAt: number) => {
    safeStorage.setItem(`rate_limit_${type}`, JSON.stringify({ expiresAt }));
  },
  
  getRateLimitInfo: (type: 'signup' | 'login'): { isRateLimited: boolean; remainingSeconds: number } => {
    const stored = safeStorage.getItem(`rate_limit_${type}`);
    if (!stored) return { isRateLimited: false, remainingSeconds: 0 };
    
    try {
      const { expiresAt } = JSON.parse(stored);
      const remaining = Math.ceil((expiresAt - Date.now()) / 1000);
      
      if (remaining <= 0) {
        safeStorage.removeItem(`rate_limit_${type}`);
        return { isRateLimited: false, remainingSeconds: 0 };
      }
      
      return { isRateLimited: true, remainingSeconds: remaining };
    } catch {
      return { isRateLimited: false, remainingSeconds: 0 };
    }
  },
  
  clearRateLimit: (type: 'signup' | 'login') => {
    safeStorage.removeItem(`rate_limit_${type}`);
  }
};
